import React, { useRef, useState } from 'react';
import Button from './Button';
import { useImageUpload } from '../../hooks/useImageUpload';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const ImageUploadButton = ({
  onUploadSuccess,
  onUploadError,
  buttonText = 'Upload',
  buttonVariant = 'outline',
  iconOnly = false,
  size = 'default',
  className = '',
  disabled = false,
  weddingId,
}) => {
  const fileInputRef = useRef(null);
  const { uploadImage, uploading, error, reset } = useImageUpload(weddingId);
  const [validationError, setValidationError] = useState(null);

  const validateFile = (file) => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Invalid file type. Allowed: JPEG, PNG, WebP, GIF`;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(2);
      return `File too large (${sizeMB}MB). Maximum size is 10MB`;
    }

    return null;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setValidationError(null);
    reset();

    // Validate file
    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      if (onUploadError) {
        onUploadError(error);
      }
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Upload file
    try {
      const result = await uploadImage(file);
      if (onUploadSuccess) {
        onUploadSuccess(result.url);
      }
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      if (onUploadError) {
        onUploadError(err.message);
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const displayError = validationError || error;

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || uploading}
      />
      <Button
        type="button"
        variant={buttonVariant}
        size={size}
        onClick={handleClick}
        disabled={disabled || uploading}
        loading={uploading}
        iconName={iconOnly ? 'ImagePlus' : undefined}
        className={iconOnly ? 'p-2' : ''}
      >
        {!iconOnly && buttonText}
      </Button>
      {displayError && (
        <p className="text-xs text-error mt-1">{displayError}</p>
      )}
    </div>
  );
};

export default ImageUploadButton;
